import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import {
  FiAward,
  FiBookOpen,
  FiClock,
  FiRefreshCw,
  FiTrendingUp,
  FiUsers,
} from "react-icons/fi";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import { db } from "../../firebase/firebaseConfig";
import { useAuth } from "../../contexts/AuthContext";

ChartJS.register(
  BarElement,
  CategoryScale,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip
);

function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value.seconds) return new Date(value.seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
  const date = toDate(value);
  if (!date) return "--";
  return date.toLocaleDateString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatShortDate(value) {
  const date = toDate(value);
  if (!date) return "--";
  return date.toLocaleDateString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
  });
}

function getTimeMs(value) {
  const date = toDate(value);
  return date ? date.getTime() : 0;
}

function getPercent(item) {
  if (typeof item?.percent === "number" && Number.isFinite(item.percent)) {
    return Math.round(item.percent);
  }

  const correct = Number(item?.score?.correct ?? item?.correct);
  const total = Number(item?.score?.total ?? item?.total);
  if (Number.isFinite(correct) && Number.isFinite(total) && total > 0) {
    return Math.round((correct / total) * 100);
  }

  return 0;
}

function getDurationSeconds(item) {
  const value = item?.duration ?? item?.timeSpent ?? item?.elapsedSeconds;
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  return Math.round(seconds);
}

function getAttemptKind(item) {
  const isAssigned = item?.isAssigned || item?.type === "assignment" || item?.source === "assignment";
  return isAssigned ? "Bài được giao" : "Tự luyện";
}

function formatDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return "--";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins >= 60) {
    const hours = Math.floor(mins / 60);
    const remMins = mins % 60;
    return remMins ? `${hours}h ${remMins}'` : `${hours}h`;
  }
  return mins > 0 ? `${mins}' ${secs}s` : `${secs}s`;
}

function getStudentName(student) {
  return student?.displayName || student?.name || student?.fullName || student?.email || "Học viên";
}

function getEnrollmentClassName(item) {
  const classInfo = item?.classInfo || {};
  return classInfo?.name || item?.className || item?.classTitle || item?.classCode || item?.classId || "Lớp học";
}

function getTeacherName(teacher) {
  return (
    teacher?.display?.name ||
    teacher?.displayName ||
    teacher?.name ||
    teacher?.email ||
    teacher?.uid ||
    teacher?.id ||
    "Giáo viên"
  );
}

async function fetchClassSummary(classId) {
  if (!classId) return null;

  const classSnap = await getDoc(doc(db, "classes", classId));
  if (!classSnap.exists()) return null;

  const teachersSnap = await getDocs(collection(db, "classes", classId, "teachers"));
  const teachers = teachersSnap.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  }));

  return {
    id: classSnap.id,
    ...classSnap.data(),
    teachers,
  };
}

function SummaryCard({ icon: Icon, label, value, hint }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-text2">{label}</div>
          <div className="mt-1 text-2xl font-bold text-text">{value}</div>
          {hint ? <div className="mt-1 text-xs text-text2">{hint}</div> : null}
        </div>
        <div className="rounded-lg bg-primary/10 p-2 text-primary">
          <Icon size={20} />
        </div>
      </div>
    </div>
  );
}

function ChartPanel({ title, children, empty }) {
  return (
    <section className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <h2 className="font-semibold text-text">{title}</h2>
      <div className="mt-4 h-72">
        {empty ? (
          <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-border bg-bg px-4 text-center text-sm text-text2">
            Chưa đủ dữ liệu để vẽ biểu đồ.
          </div>
        ) : (
          children
        )}
      </div>
    </section>
  );
}

export default function ParentChildrenPage() {
  const { user, userExt } = useAuth();
  const [children, setChildren] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [histories, setHistories] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState("");

  const configuredChildIds = useMemo(() => {
    const sources = [userExt?.childIds, userExt?.studentIds, userExt?.children];
    return Array.from(
      new Set(
        sources
          .flatMap((value) => (Array.isArray(value) ? value : []))
          .map((value) => (typeof value === "string" ? value : value?.id || value?.uid))
          .filter(Boolean)
      )
    );
  }, [userExt]);

  const loadChildren = async () => {
    if (!user?.uid) return;
    setLoadingChildren(true);
    setError("");
    try {
      const snap = await getDocs(
        query(collection(db, "users"), where("parentIds", "array-contains", user.uid))
      );
      const rows = snap.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .sort((a, b) => getStudentName(a).localeCompare(getStudentName(b), "vi"));

      setChildren(rows);
      setSelectedId((prev) => (rows.some((item) => item.id === prev) ? prev : rows[0]?.id || ""));
    } catch (err) {
      console.error("Cannot load parent children:", err);
      setChildren([]);
      setError("Chưa tải được danh sách học viên được liên kết với tài khoản phụ huynh.");
    } finally {
      setLoadingChildren(false);
    }
  };

  useEffect(() => {
    loadChildren();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  useEffect(() => {
    if (!selectedId) {
      setHistories([]);
      setEnrollments([]);
      return;
    }

    let cancelled = false;
    const loadDetail = async () => {
      setLoadingDetail(true);
      try {
        const [historySnap, enrollmentSnap] = await Promise.all([
          getDocs(
            query(
              collection(db, "test_histories"),
              where("userId", "==", selectedId),
              orderBy("submittedAt", "desc"),
              limit(80)
            )
          ),
          getDocs(
            query(
              collectionGroup(db, "enrollments"),
              where("uid", "==", selectedId)
            )
          ),
        ]);

        if (cancelled) return;
        setHistories(historySnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
        const activeEnrollments = enrollmentSnap.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((item) => !item.status || item.status === "active" || item.status === "enrolled");
        const classEntries = await Promise.all(
          activeEnrollments.map(async (item) => {
            try {
              return [item.classId, await fetchClassSummary(item.classId)];
            } catch (err) {
              console.warn("Cannot load parent class summary:", item.classId, err);
              return [item.classId, null];
            }
          })
        );
        const classMap = new Map(classEntries);

        if (cancelled) return;
        setEnrollments(
          activeEnrollments.map((item) => ({
            ...item,
            classInfo: classMap.get(item.classId) || null,
          }))
        );
      } catch (err) {
        console.error("Cannot load child detail:", err);
        if (!cancelled) {
          setHistories([]);
          setEnrollments([]);
          setError("Một số dữ liệu của học viên chưa tải được. Kiểm tra Firestore rules/index nếu lỗi vẫn xuất hiện.");
        }
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    };

    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const selectedChild = children.find((item) => item.id === selectedId) || null;

  const stats = useMemo(() => {
    const total = histories.length;
    const scores = histories.map(getPercent);
    const durations = histories.map(getDurationSeconds).filter((value) => typeof value === "number");
    const avg = total ? Math.round(scores.reduce((sum, value) => sum + value, 0) / total) : 0;
    const best = total ? Math.max(...scores) : 0;
    const totalDuration = durations.reduce((sum, value) => sum + value, 0);
    const latest = histories[0] || null;

    return { total, avg, best, totalDuration, latest };
  }, [histories]);

  const chartRows = useMemo(() => {
    return histories
      .map((item) => ({
        id: item.id,
        title: item.title || item.testTitle || "Bài kiểm tra",
        label: formatShortDate(item.submittedAt || item.createdAt),
        percent: getPercent(item),
        durationMinutes: (() => {
          const duration = getDurationSeconds(item);
          return duration ? Math.round((duration / 60) * 10) / 10 : null;
        })(),
        kind: getAttemptKind(item),
        submittedMs: getTimeMs(item.submittedAt || item.createdAt),
      }))
      .sort((a, b) => a.submittedMs - b.submittedMs);
  }, [histories]);

  const latestChartRows = useMemo(() => chartRows.slice(-12), [chartRows]);

  const progressChartData = useMemo(
    () => ({
      labels: latestChartRows.map((row, index) => `${index + 1}. ${row.label}`),
      datasets: [
        {
          label: "Điểm (%)",
          data: latestChartRows.map((row) => row.percent),
          borderColor: "#2563eb",
          backgroundColor: "#2563eb",
          tension: 0.3,
          yAxisID: "score",
        },
        {
          label: "Thời gian (phút)",
          data: latestChartRows.map((row) => row.durationMinutes),
          borderColor: "#f59e0b",
          backgroundColor: "#f59e0b",
          tension: 0.3,
          yAxisID: "duration",
        },
      ],
    }),
    [latestChartRows]
  );

  const progressChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { position: "bottom" },
        tooltip: {
          callbacks: {
            afterBody: (items) => {
              const row = latestChartRows[items[0]?.dataIndex];
              return row ? [row.kind, row.title] : [];
            },
          },
        },
      },
      scales: {
        score: {
          beginAtZero: true,
          max: 100,
          ticks: { callback: (value) => `${value}%` },
        },
        duration: {
          beginAtZero: true,
          position: "right",
          grid: { drawOnChartArea: false },
          ticks: { callback: (value) => `${value}'` },
        },
      },
    }),
    [latestChartRows]
  );

  const durationRows = useMemo(
    () => chartRows.filter((row) => row.durationMinutes !== null).slice(-10),
    [chartRows]
  );

  const durationChartData = useMemo(
    () => ({
      labels: durationRows.map((row, index) => `${index + 1}. ${row.label}`),
      datasets: [
        {
          label: "Thời gian làm bài (phút)",
          data: durationRows.map((row) => row.durationMinutes),
          backgroundColor: durationRows.map((row) =>
            row.kind === "Bài được giao" ? "#2563eb" : "#10b981"
          ),
          borderRadius: 6,
        },
      ],
    }),
    [durationRows]
  );

  const durationChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            afterBody: (items) => {
              const row = durationRows[items[0]?.dataIndex];
              return row ? [row.kind, row.title] : [];
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: { callback: (value) => `${value}'` },
        },
      },
    }),
    [durationRows]
  );

  const showLinkHint = !loadingChildren && children.length === 0 && configuredChildIds.length > 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text">Quản lý con</h1>
          <p className="mt-1 text-sm text-text2">
            Theo dõi lớp học, kết quả làm bài và tiến độ học tập của học viên được liên kết.
          </p>
        </div>
        <button
          type="button"
          onClick={loadChildren}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm font-semibold text-text hover:bg-primary/10"
        >
          <FiRefreshCw /> Tải lại
        </button>
      </div>

      {error ? (
        <div className="rounded-lg border border-warning bg-warning/10 px-4 py-3 text-sm text-text">
          {error}
        </div>
      ) : null}

      {loadingChildren ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center text-text2 shadow-sm">
          Đang tải danh sách học viên...
        </div>
      ) : children.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-primary/10 p-2 text-primary">
              <FiUsers size={22} />
            </div>
            <div>
              <div className="font-semibold text-text">Chưa có học viên được liên kết</div>
              <p className="mt-1 text-sm text-text2">
                Quản trị viên cần liên kết phụ huynh với học viên trong màn hình quản lý người dùng.
              </p>
              {showLinkHint ? (
                <p className="mt-2 text-sm text-warning">
                  Tài khoản phụ huynh đang có danh sách con trong hồ sơ, nhưng chưa tìm thấy học viên tương ứng.
                </p>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="grid gap-3 md:grid-cols-3">
            {children.map((child) => {
              const active = child.id === selectedId;
              return (
                <button
                  key={child.id}
                  type="button"
                  onClick={() => setSelectedId(child.id)}
                  className={`rounded-lg border p-4 text-left shadow-sm transition ${
                    active
                      ? "border-primary bg-primary/10"
                      : "border-border bg-card hover:bg-primary/5"
                  }`}
                >
                  <div className="font-semibold text-text">{getStudentName(child)}</div>
                  <div className="mt-1 text-sm text-text2">{child.email || child.phone || child.id}</div>
                  <div className="mt-3 inline-flex rounded bg-bg px-2 py-1 text-xs font-semibold text-text2">
                    {child.role || "student"}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="grid gap-3 md:grid-cols-4">
            <SummaryCard icon={FiBookOpen} label="Lượt làm bài" value={stats.total} hint={selectedChild ? getStudentName(selectedChild) : ""} />
            <SummaryCard icon={FiTrendingUp} label="Điểm trung bình" value={`${stats.avg}%`} hint="Tính theo các bài đã nộp" />
            <SummaryCard icon={FiAward} label="Điểm cao nhất" value={`${stats.best}%`} hint="Kết quả tốt nhất" />
            <SummaryCard icon={FiClock} label="Tổng thời gian" value={formatDuration(stats.totalDuration)} hint={stats.latest ? `Mới nhất: ${formatDate(stats.latest.submittedAt || stats.latest.createdAt)}` : "Chưa có dữ liệu"} />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <ChartPanel title="Quá trình học theo thời gian" empty={latestChartRows.length < 2}>
              <Line data={progressChartData} options={progressChartOptions} />
            </ChartPanel>
            <ChartPanel title="Thời gian làm bài gần đây" empty={durationRows.length === 0}>
              <Bar data={durationChartData} options={durationChartOptions} />
            </ChartPanel>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <section className="rounded-lg border border-border bg-card p-4 shadow-sm lg:col-span-2">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-semibold text-text">Kết quả gần đây</h2>
                {loadingDetail ? <span className="text-sm text-text2">Đang tải...</span> : null}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-text2">
                      <th className="py-2 pr-3">Bài làm</th>
                      <th className="py-2 pr-3">Thời gian nộp</th>
                      <th className="py-2 pr-3">Điểm</th>
                      <th className="py-2 pr-3">Thời gian làm</th>
                    </tr>
                  </thead>
                  <tbody>
                    {histories.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="py-8 text-center text-text2">
                          Chưa có lịch sử làm bài.
                        </td>
                      </tr>
                    ) : (
                      histories.slice(0, 10).map((item) => (
                        <tr key={item.id} className="border-b border-border last:border-0">
                          <td className="py-3 pr-3 font-semibold text-text">{item.title || item.testTitle || "Bài kiểm tra"}</td>
                          <td className="py-3 pr-3 text-text2">{formatDate(item.submittedAt || item.createdAt)}</td>
                          <td className="py-3 pr-3 text-text">
                            {item?.score?.correct ?? item.correct ?? 0}/{item?.score?.total ?? item.total ?? 0} ({getPercent(item)}%)
                          </td>
                          <td className="py-3 pr-3 text-text2">{formatDuration(getDurationSeconds(item))}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <aside className="space-y-4">
              <section className="rounded-lg border border-border bg-card p-4 shadow-sm">
                <h2 className="font-semibold text-text">Lớp đang học</h2>
                <div className="mt-3 space-y-2">
                  {enrollments.length === 0 ? (
                    <div className="rounded bg-bg px-3 py-3 text-sm text-text2">Chưa có lớp đang hoạt động.</div>
                  ) : (
                    enrollments.map((item) => (
                      <div key={item.id} className="rounded border border-border bg-bg px-3 py-2">
                        <div className="font-semibold text-text">{getEnrollmentClassName(item)}</div>
                        <div className="mt-1 text-xs text-text2">
                          Giáo viên:{" "}
                          {item.classInfo?.teachers?.length
                            ? item.classInfo.teachers.map(getTeacherName).join(", ")
                            : "Chưa gán"}
                        </div>
                        <div className="mt-1 text-xs text-text2">Trạng thái: {item.status || "active"}</div>
                      </div>
                    ))
                  )}
                </div>
              </section>

              <section className="rounded-lg border border-border bg-card p-4 shadow-sm">
                <h2 className="font-semibold text-text">Điểm gần đây</h2>
                <div className="mt-4 space-y-3">
                  {chartRows.length === 0 ? (
                    <div className="rounded bg-bg px-3 py-3 text-sm text-text2">Chưa đủ dữ liệu.</div>
                  ) : (
                    chartRows.slice(-8).map((row) => (
                      <div key={row.id} className="grid grid-cols-[42px_1fr_42px] items-center gap-2 text-xs">
                        <span className="text-text2">{row.label}</span>
                        <div className="h-2 rounded bg-bg">
                          <div className="h-2 rounded bg-primary" style={{ width: `${Math.max(row.percent, 4)}%` }} />
                        </div>
                        <span className="text-right font-semibold text-text">{row.percent}%</span>
                      </div>
                    ))
                  )}
                </div>
              </section>
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
